import React, { useState, useCallback, useEffect } from 'react';
import Input from './Input';
import useCopy from '../util/useCopy';
import {
  useHandleValidation,
  handleValidationErrorCopyKeys,
} from '../util/useHandleValidation';
import { generateRandomUsername } from '../util/generateRandomUsername';

type HandleInputProps = {
  currentHandle: string;
  onChange: (state: { value: string; isValid: boolean }) => void;
  disabled?: boolean;
  autoFocus?: boolean;
};

export const HandleInput = ({
  currentHandle,
  onChange,
  disabled,
  autoFocus,
}: HandleInputProps) => {
  const copy = useCopy();
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneratedUsername, setLastGeneratedUsername] = useState<string | null>(null);

  const { setHandleText, normalizedHandle, validationError, canSave } =
    useHandleValidation(currentHandle);

  useEffect(() => {
    onChange({ value: normalizedHandle, isValid: canSave });
  }, [normalizedHandle, canSave, onChange]);

  const handlePickForMe = useCallback(async () => {
    setIsGenerating(true);
    try {
      let username = await generateRandomUsername();
      setHandleText(username);
      setLastGeneratedUsername(username);
    } catch (error) {
      console.error('Failed to generate username:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [setHandleText]);

  const showGenerateLink =
    !normalizedHandle ||
    normalizedHandle === lastGeneratedUsername ||
    normalizedHandle === currentHandle;

  return (
    <>
      <div className="mb-2 text-left">
        {validationError ? (
          copy[handleValidationErrorCopyKeys[validationError]]
        ) : showGenerateLink ? (
          <span
            className={
              isGenerating
                ? 'text-sm font-medium text-rose-500 opacity-50'
                : 'cursor-pointer text-sm font-medium text-rose-500'
            }
            onClick={isGenerating ? undefined : handlePickForMe}
          >
            {normalizedHandle === lastGeneratedUsername
              ? copy.generateAnother
              : copy.pickOneForMe}
          </span>
        ) : (
          <>&ensp;</>
        )}
      </div>
      <Input
        stacked
        autoFocus={autoFocus}
        autoCorrect="off"
        autoCapitalize="none"
        value={normalizedHandle}
        disabled={disabled || isGenerating}
        onChange={setHandleText}
      />
    </>
  );
};
